import TalentDisciplineForm from "../components/TalentDisciplineForm";
import TalentSkillForm from "../components/TalentSkillForm";
import TalentAppStep from "../components/TalentAppStep";
import Discipline from "../../../models/Discipline";
import User from "../../../models/User";
import Form from '../../../components/form/Form'
import React, { useRef } from "react";
import { Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import AIResumeScoreModal, { AIResumeUploadAndScoreModal } from "../components/AIResumeScoreModal";
import AIIcon from "../../../components/AiIcon";


const MAX_RELATIONS = 10;

/**
 * AI Resume Scoring Button Component
 * Triggers AI-powered role matching against user's resume
 */
const AIResumeScoreButton = ({ disciplines, onSuccess }) => {
    const scoreModalRef = useRef();
    const uploadModalRef = useRef();

    const disciplinesMissingScores = disciplines.filter(d => !Boolean(d.resume_match_breakdown));
    const allScored = disciplinesMissingScores.length === 0;

    return (
        <>
            {/* Resume Score Status */}
            <div className="bg-red100 p4 mb-5">

                <div className="p-5 rounded-xl sm:flex items-center gap-5 bg-white border-l-[5px] border-primary mb-10 shadow-md">
                    <AIIcon className="h-11 w-11" />

                    <div className="flex-1">

                        {disciplines.length - disciplinesMissingScores.length <= 0 ? (
                            <>
                                <p className="font-bold text-lg mb-2">
                                    Your resume has not been scored yet
                                </p>
                                <p>
                                    Wrex, our AI assistant, will analyze your resume and generate a Resume Score for each role below. <strong>Upload an updated resume</strong> anytime to refresh your scores.
                                </p>

                            </>
                        ) : (
                            <>
                                <div className="flex gap-3 items-center mb-2">
                                    <p className="font-bold text-lg">
                                        Resume scored across {disciplines.length - disciplinesMissingScores.length} roles
                                    </p>
                                    <p className="text-xs py-0.5 px-2 rounded-xl bg-[#EBE1F7] text-primary">AI Scored</p>
                                </div>
                                <p>
                                    Wrex, our AI assistant, analyzed your resume and generated a Resume Score for each role below. <strong>Upload an updated resume</strong> anytime to refresh your scores.
                                </p>

                            </>
                        )}
                    </div>

                    {!allScored && (
                        <Button
                            type="primary"
                            onClick={() => scoreModalRef.current?.open()}
                            className="border-0 shadow-md pl-2"
                            icon={<UploadOutlined />}
                        >
                            Update Now
                        </Button>
                    )}

                    {allScored && (
                        <Button
                            type="primary"
                            onClick={() => uploadModalRef.current?.open()}
                            className="border-0 shadow-md pl-2"
                            icon={<UploadOutlined />}
                        >
                            Upload New Resume & Rescore
                        </Button>
                    )}
                </div>
            </div>

            <AIResumeScoreModal
                ref={scoreModalRef}
                disciplines={disciplines}
                onSuccess={onSuccess}
            />
            <AIResumeUploadAndScoreModal
                ref={uploadModalRef}
                disciplines={disciplines}
                onSuccess={onSuccess}
            />
        </>
    );
};

export default class DisciplineSkillsStep extends TalentAppStep {
    static path = '/roles-and-skills';
    static LastUpdatedMethod = 'lastUpdatedSkills';

    static HasRequirements = true;
    /**
     * The title shown on the form
     */
    static StepTitle = TB.trans('talent.roles_skills.step_title');
    /**
     * The subtitle shown on the form
     */
    static StepSubtitle = 'What role(s) can you play on Wripple\'s client projects? By selecting ' +
        'both roles and related skills, this will help determine how you get matched to client projects. ' +
        'Be sure to set your bill rate and years of experience per role!';
    /**
     * The start page info tile
     */
    static StepInfo = TB.trans('talent.roles_skills.step_info');

    /**
     * The Step Section to be grouped in after application approval
     */
    static StepSection = 'Expertise';

    async componentDidMount() {
        super.componentDidMount();
        let importedDisciplines = User.getImportedItemsFromResumeForUser(this.props.user.id, 'talent_disciplines');
        let currentDisciplines = this.props.user.talent_disciplines || [];
        if (currentDisciplines.length > 0 && importedDisciplines?.length > 0 && !this.props.user.isApplicationApproved()) {
            let importedIds = TB.map(importedDisciplines, dis => parseInt(dis.id))
            let firstIndex = TB.findIndex(currentDisciplines, dis => importedIds?.includes(dis?.id));
            if (firstIndex >= 0 && !TB.isNaN(firstIndex)) {
                await this.handleChildFormEdit(this.childFormIndexName(firstIndex, 'talent_disciplines'), true)
            }
        }
        const myRequest = await TB.request(TB.api_endpoint('/api/talent-disciplines')).get();
        this.setState({ discipline_categories: myRequest.success?.data.discipline_categories });
    }
    /**
     * Show brief details for user
     */
    static ProfileSectionSummary = (user) => {
        return `${user.talent_disciplines?.length} Roles/${user.skills?.length} Skills`;
    };

    /**
     * The step needed before editing
     */
    static StepNeedsValidation(user) {
        if (!user.validateDisciplineSkills()) {
            return this;
        }

        return false;
    };

    static hasCompleted(user) {
        let items = TB.pick(user, ['talent_disciplines', 'skills']);

        items = TB.flatten(TB.values(items));

        items = TB.filter(items, item => TB.isDefined(item?.id))
        if (!items.length) {
            return false;
        }
        if (!user.isApplicationApproved()) {
            for (let discipline of user.talent_disciplines) {
                if (Discipline.skillsFor(discipline?.id)?.length && !user.selectedSkillsForDiscipline(discipline?.id)?.length) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * Check if we should prevent application from saving and show alert
     */
    static async getValidProps(user) {


        if (!user.talent_disciplines?.length) {
            await TB.sysAlert('talent.disciplines_skills_step_role_required')
            return false;
        }
        if (!user.skills?.length) {
            await TB.sysAlert('talent.disciplines_skills_step_skill_required')
            return false;
        }
        if (!user.isApplicationApproved()) {
            for (let discipline of user.talent_disciplines) {

                if (Discipline.skillsFor(discipline?.id)?.length && !user.selectedSkillsForDiscipline(discipline?.id)?.length) {
                    if (document.getElementById(`talent_disciplines-form-${discipline.id}`)) {
                        $('html, body').animate({ scrollTop: $(`#talent_disciplines-form-${discipline.id}`).offset().top - 20 }, 1000);
                    }
                    return false;
                }
            }
        }
        return user.validateDisciplineSkills()
    };

    /**
     * @param {User} user
     * @param {User} auth
     * @returns {Object}
     */
    static reviewMapping(user, auth) {

        let userSkills = [...user.skills];
        let review = {};
        TB.map([...user.talent_disciplines], ({ id, name, self_score, hourly_rate }, index) => {
            let disciplineReview = {
                [TB.trans('talent.roles_skills.review_mapping.role')]: name,
                [TB.trans('talent.roles_skills.review_mapping.years_exp')]: laravel.talent_scoring_options[self_score],
            }
            // show hourly rate for non supplier talent or talent resource managers
            if ((auth?.canManageClientResourceTalent?.() || !user.isTalentSupplierResource()) && !TB.isNaN(parseInt(hourly_rate))) {
                disciplineReview[TB.trans('talent.roles_skills.review_mapping.rate')] = TB.trans('talent.roles_skills.review_mapping.currency_symbol') + parseInt(hourly_rate)
            }
            let disciplineSkills = TB.keyBy(Discipline.skillsFor(id), 'id');

            let indexes = []
            TB.map(userSkills, (skill, index) => {
                if (TB.isDefined(disciplineSkills[skill.id])) {
                    indexes.push(index)
                }
            })

            if (indexes.length) {
                disciplineSkills = TB.pullAt(userSkills, indexes);
                disciplineReview[TB.trans('talent.roles_skills.review_mapping.skills')] = TB.join(disciplineSkills.map(skill => {

                    userSkills = TB.reject(userSkills, ['id', skill.id])

                    return `${skill.name}, ${laravel.talent_scoring_options[skill.self_score]}`
                }), "\n")
            }

            review[`Role #${index + 1}`] = disciplineReview
        })

        if (userSkills.length) {
            review.Skills = TB.join(userSkills.map(skill =>
                `${skill.name}, ${laravel.talent_scoring_options[skill.self_score]}`
            ), "\n")
        }
        return review
    }

    /**
     * The fields mapping for the form
     */
    formFields = () => {
        return [];
    };

    /**
     * Render the form
     *
     * @returns {Form}
     */
    renderFormFooter() {
        let {
            talent_disciplines = [],
            skills = []
        } = this.props.user;

        let validTalentDisciplines = talent_disciplines.filter(({ id }) => TB.isDefined(id));
        return (
            <div className="pb-6">

                {/* AI Resume Scoring Button */}
                {validTalentDisciplines.length > 0 && (
                    <AIResumeScoreButton
                        disciplines={talent_disciplines}
                        onSuccess={async (data) => {
                            // Refresh user data after successful scoring
                            if (this.props.onChange) {
                                await this.onChange({ ...this.props.user, ...data });
                            }
                        }}
                    />
                )}

                {/* <TalentAISkillForm myList={this.state.myList} /> */}

                {this.renderChildForm({
                    displayName: "Role",
                    ChildFormComponent: TalentDisciplineForm,
                    userProp: 'talent_disciplines',
                    formProps: {
                        discipline_categories: [...laravel.discipline_categories],
                        skills,
                        disciplines: validTalentDisciplines,
                        onChange: this.onChange.bind(this),
                    },
                    maxRelations: MAX_RELATIONS,
                })}


                {(skills.length > 0 || validTalentDisciplines.length > 0) && (
                    <TalentSkillForm
                        key={"add-custom-skills"}
                        user={this.props.user}
                        disabled={this.props.disabled}
                        onChange={async (user) => {

                            let currentUser = TB.pick({ ...this.props.user }, 'skills');

                            await this.onChange(TB.cloneDeep(user));

                            // let didSubmit = await this.props.onSubmit(this.props.user, false, true);
                            //
                            // if (!didSubmit) {
                            //     TB.showServerErrorToast();
                            //     await this.onChange(currentUser);
                            // } else {
                            //     TB.toast('Profile Updated')
                            // }
                        }}
                        canEdit={!this.isEditingChildForm() && validTalentDisciplines.length > 0}
                        isEditing={!this.isEditingChildForm() && validTalentDisciplines.length > 0}
                    />
                )}

            </div>
        );
    }

    /**
     * Are requirements completed?
     *
     * @returns boolean
     */
    isRequirementComplete() {
        const hasDisciplines = TB.filter(this.props.user?.talent_disciplines || [], dis => !!dis?.id)?.length >= 1;
        const hasSkills = TB.filter(this.props.user?.skills || [], skill => !!skill?.id)?.length >= 1;
        return hasSkills && hasDisciplines;
    }
    /**
     * Requirement messaging
     *
     * @returns string
     */
    requirementInfo() {
        return `At least 1 role is required. Max of ${MAX_RELATIONS}.`;
        // return 'At least 1 role and skills are required.';
    }

    addNewArrayProp = async (propName) => {
        if (this.props.user?.talent_disciplines?.length > MAX_RELATIONS) {
            return TB.alert('warning', 'Max roles limit reached', `A max of ${MAX_RELATIONS} roles are allowed (pending approval or approved)`)
        }

        let models = TB.get({ ...this.props.user }, propName, []);
        models.push({});

        await this.props.onChange({ [propName]: models });
        return this.handleChildFormEdit(
            this.childFormIndexName(models.length - 1, propName), true
        )
    }
}